import { Component } from '@theme/component';

/**
 * Back-to-top link (TROOP-889, TROOP-860, snippets/armypoint-back-to-top.liquid): brand overview
 * and collection pages. The link itself is a plain anchor to its target; this component only shows
 * it once the target has scrolled out of view above the viewport (`data-visible`), and hides it
 * again when the target is back in view.
 *
 * @typedef {object} BackToTopRefs
 * @property {HTMLAnchorElement} link - Link whose hash names the target element.
 *
 * @extends {Component<BackToTopRefs>}
 */
class BackToTop extends Component {
  requiredRefs = ['link'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#observe();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /**
   * Watches the link target; idempotent, so reconnects and section re-renders keep one observer.
   */
  #observe() {
    this.#observer?.disconnect();

    const id = decodeURIComponent(this.refs.link.hash.slice(1));
    const target = id ? document.getElementById(id) : null;
    if (!target) return;

    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.toggleAttribute('data-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    this.#observer.observe(target);
  }
}

if (!customElements.get('armypoint-back-to-top')) {
  customElements.define('armypoint-back-to-top', BackToTop);
}
